import React from "react";
import * as Yup from "yup";
import { EDIT_UNIT_LEADER_DETAILS } from "./constants";
import { Form, Formik } from "formik";
import Button from "../../Molecules/button/Button";
import Input from "../../Atoms/Input/Input";
import ContactInput from "../../Atoms/Input/ContactInput";
import UnitNumberSelect from "../../Molecules/unit-number-select/UnitNumberSelect";
import { VALIDATION_CONSTANTS } from "../../../utils/validation-constants/validationConstants";
import { TEXT_CONSTANTS } from "../../../utils/text-constants/textConstants";

interface EditUnitLeaderProps {
  defaultSelectedData?: any;
  handleOnSubmittedData?: (formData: any) => void;
  handleClose: () => void;
}

const EditUnitLeader = ({
  defaultSelectedData,
  handleOnSubmittedData,
  handleClose,
}: EditUnitLeaderProps) => {
  // All fields are optional (the goal is to fill in / correct missing info);
  // format is validated only when a value is present.
  const validationSchema = Yup.object().shape({
    [EDIT_UNIT_LEADER_DETAILS.LEADER_NAME]: Yup.string().matches(
      VALIDATION_CONSTANTS.CHARACTER,
      {
        message: "Unit leader name can only contain characters",
        excludeEmptyString: true,
      }
    ),
    [EDIT_UNIT_LEADER_DETAILS.LEADER_EMAIL]: Yup.string().matches(
      VALIDATION_CONSTANTS.EMAIL,
      { message: "Enter a valid email address", excludeEmptyString: true }
    ),
    [EDIT_UNIT_LEADER_DETAILS.LEADER_CONTACT]: Yup.string().matches(
      VALIDATION_CONSTANTS.CONTACT_NUMBER,
      { message: "Invalid contact number", excludeEmptyString: true }
    ),
  });

  const commonGridStyle = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "24px 16px",
  };

  return (
    <Formik
      initialValues={{
        [EDIT_UNIT_LEADER_DETAILS.CLINIC_LOCATION]: `${defaultSelectedData?.id ? defaultSelectedData?.id : ""}`,
        [EDIT_UNIT_LEADER_DETAILS.UNIT_NUMBER]: `${defaultSelectedData?.unitNumber ? defaultSelectedData?.unitNumber : ""}`,
        [EDIT_UNIT_LEADER_DETAILS.LEADER_NAME]: `${defaultSelectedData?.leaderName ? defaultSelectedData?.leaderName : ""}`,
        [EDIT_UNIT_LEADER_DETAILS.LEADER_EMAIL]: `${defaultSelectedData?.email ? defaultSelectedData?.email : ""}`,
        [EDIT_UNIT_LEADER_DETAILS.LEADER_CONTACT]: `${defaultSelectedData?.leaderContact ? defaultSelectedData?.leaderContact : ""}`,
      }}
      validationSchema={validationSchema}
      enableReinitialize
      onSubmit={(values, { setSubmitting }) => {
        try {
          handleOnSubmittedData && handleOnSubmittedData(values);
        } catch (error) {
          console.log(error);
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({
        errors,
        values,
        touched,
        setFieldValue,
        handleBlur,
        handleSubmit,
      }) => {
        return (
          <Form
            onSubmit={handleSubmit}
            className="h-full flex flex-col justify-between rounded-lg"
          >
            <div className="w-full flex flex-col rounded-lg">
              <div>
                <div className="mb-6">
                  <UnitNumberSelect
                    label="Unit Number"
                    allowCreate
                    defaultValue={
                      defaultSelectedData?.id
                        ? {
                            label: defaultSelectedData?.unitNumber,
                            value: defaultSelectedData?.id,
                          }
                        : null
                    }
                    onChange={(record) => {
                      setFieldValue(
                        EDIT_UNIT_LEADER_DETAILS.CLINIC_LOCATION,
                        record?.id || ""
                      );
                      setFieldValue(
                        EDIT_UNIT_LEADER_DETAILS.UNIT_NUMBER,
                        record?.unitNumber || ""
                      );
                      setFieldValue(
                        EDIT_UNIT_LEADER_DETAILS.LEADER_NAME,
                        record?.leaderName || ""
                      );
                      setFieldValue(
                        EDIT_UNIT_LEADER_DETAILS.LEADER_EMAIL,
                        record?.email || ""
                      );
                      setFieldValue(
                        EDIT_UNIT_LEADER_DETAILS.LEADER_CONTACT,
                        record?.leaderContact || ""
                      );
                    }}
                  />
                </div>
                <div style={commonGridStyle}>
                  <Input
                    textFieldLabel="Unit Leader Name"
                    name={EDIT_UNIT_LEADER_DETAILS.LEADER_NAME}
                    value={values[EDIT_UNIT_LEADER_DETAILS.LEADER_NAME]}
                    onChangeCb={(e) => {
                      setFieldValue(
                        EDIT_UNIT_LEADER_DETAILS.LEADER_NAME,
                        e.target.value
                      );
                    }}
                    onBlur={handleBlur}
                    size="small"
                    error={
                      touched[EDIT_UNIT_LEADER_DETAILS.LEADER_NAME] &&
                      Boolean(errors[EDIT_UNIT_LEADER_DETAILS.LEADER_NAME])
                    }
                    helperText={
                      touched[EDIT_UNIT_LEADER_DETAILS.LEADER_NAME]
                        ? errors[EDIT_UNIT_LEADER_DETAILS.LEADER_NAME]
                        : undefined
                    }
                  />
                  <Input
                    textFieldLabel="Unit Leader Email"
                    name={EDIT_UNIT_LEADER_DETAILS.LEADER_EMAIL}
                    value={values[EDIT_UNIT_LEADER_DETAILS.LEADER_EMAIL]}
                    onChangeCb={(e) => {
                      setFieldValue(
                        EDIT_UNIT_LEADER_DETAILS.LEADER_EMAIL,
                        e.target.value
                      );
                    }}
                    onBlur={handleBlur}
                    size="small"
                    error={
                      touched[EDIT_UNIT_LEADER_DETAILS.LEADER_EMAIL] &&
                      Boolean(errors[EDIT_UNIT_LEADER_DETAILS.LEADER_EMAIL])
                    }
                    helperText={
                      touched[EDIT_UNIT_LEADER_DETAILS.LEADER_EMAIL]
                        ? errors[EDIT_UNIT_LEADER_DETAILS.LEADER_EMAIL]
                        : undefined
                    }
                  />
                  <ContactInput
                    textFieldLabel="Unit Leader Contact"
                    name={EDIT_UNIT_LEADER_DETAILS.LEADER_CONTACT}
                    value={values[EDIT_UNIT_LEADER_DETAILS.LEADER_CONTACT]}
                    onChangeCb={(value) => {
                      setFieldValue(
                        EDIT_UNIT_LEADER_DETAILS.LEADER_CONTACT,
                        value
                      );
                    }}
                    onBlur={handleBlur}
                    size="small"
                    error={
                      touched[EDIT_UNIT_LEADER_DETAILS.LEADER_CONTACT] &&
                      Boolean(errors[EDIT_UNIT_LEADER_DETAILS.LEADER_CONTACT])
                    }
                    helperText={
                      touched[EDIT_UNIT_LEADER_DETAILS.LEADER_CONTACT]
                        ? errors[EDIT_UNIT_LEADER_DETAILS.LEADER_CONTACT]
                        : undefined
                    }
                    isContactNumber={true}
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end mt-10 gap-4">
              <Button
                label={TEXT_CONSTANTS?.CANCEL}
                variant="outlined"
                onClickCb={() => {
                  handleClose();
                }}
                style={{
                  width: "120px",
                  height: "44px",
                  padding: "12px 20px",
                  fontSize: "16px",
                  backgroundColor: "white",
                  border: "none",
                  color: "#393939",
                }}
              />
              <Button
                type="submit"
                label={TEXT_CONSTANTS?.SAVE}
                variant="contained"
                onClickCb={() => {}}
                style={{
                  width: "120px",
                  height: "44px",
                  padding: "12px 20px",
                  fontSize: "16px",
                }}
              ></Button>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default EditUnitLeader;
